import { CHAT_BATCH_SIZE, CHAT_FILE_SIZE, CHAT_PREVIEW_SIZE } from '../../utils/chatMessageQueue';
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import Textarea from '@mui/joy/Textarea';
import { IconButton, LinearProgress, Sheet, Snackbar, Stack, Tooltip, Typography } from '@mui/joy';

import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import StrikethroughSRoundedIcon from '@mui/icons-material/StrikethroughSRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import InsertPhotoRoundedIcon from '@mui/icons-material/InsertPhotoRounded';
import InsertEmoticonRoundedIcon from '@mui/icons-material/InsertEmoticonRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { motion } from 'framer-motion';

import { captureRect } from './SendFlight';
import { CHAT, MOTION, ellipsis, iconColor } from './chatTheme';



// Selection is unlimited; the queue creates bounded upload requests.
export default function MessageInput(props) {
  const { textAreaValue, setTextAreaValue, onSubmit, onSendVoice, onAttach,
          participants = [], replyTo, onCancelReply,
          editing = null, onCancelEdit, disabled = false } = props;
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  // Из этого элемента «вылетает» сообщение: снимаем его геометрию в момент
  // отправки и передаём наверх, чтобы анимация стартовала точно из поля.
  const composerRef = useRef(null);
  // Превью картинок. Держим ссылки в состоянии и обязательно отзываем их
  // в cleanup: objectURL живёт до конца вкладки и течёт памятью, если про
  // него забыть.
  const [previews, setPreviews] = useState({});
  const attachments = props.attachments || [];
  const setAttachments = onAttach;
  const submitRef = useRef(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // ── Упоминания (@теги) ───────────────────────────────────────────
  const taRef = useRef(null);          // реальный <textarea> из onChange-события
  const caretRef = useRef(0);          // позиция курсора
  const [mentions, setMentions] = useState([]);        // выбранные {kind,id,name}
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');

  const mentionOptions = participants.filter((p) => {
    const q = (mentionQuery || '').toLowerCase();
    return !q || (p.name || '').toLowerCase().includes(q);
  });

  const pickMention = (p) => {
    if (disabled) return;
    const caret = caretRef.current ?? textAreaValue.length;
    const before = textAreaValue.slice(0, caret).replace(/@([^@\s]*)$/, `@${p.name} `);
    const after = textAreaValue.slice(caret);
    setTextAreaValue(before + after);
    const _kind = p.kind || 'employee';
    setMentions((prev) =>
      prev.some((x) => x.kind === _kind && x.id === p.id)
        ? prev
        : [...prev, { kind: _kind, id: p.id, name: p.name }]
    );
    setMentionOpen(false);
    setTimeout(() => taRef.current?.focus?.(), 0);
  };

  // ── Голосовые: запись ────────────────────────────────────────────
  const [recording, setRecording] = useState(false);
  const [paused, setPaused]       = useState(false);
  const [seconds, setSeconds]     = useState(0);
  const [liveBars, setLiveBars]   = useState([]);   // последние ~40 уровней 0..100
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef   = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef      = useRef(null);
  const chunksRef   = useRef([]);
  const peaksRef    = useRef([]);   // все уровни за запись → waveform
  const lastPushRef = useRef(0);
  const startedAtRef = useRef(0);
  const pausedMsRef  = useRef(0);
  const pauseStartRef = useRef(0);
  const abortRef    = useRef(null);
  const tickRef     = useRef(null);

  const MAX_SECONDS = 300; // 5 минут — авто-стоп (согласовать с бэком)

  const pickMime = () => {
    // Приоритет — форматы, которые играются на iOS Safari (mp4/aac). webm/opus
    // Safari НЕ проигрывает, поэтому берём его только крайним фолбэком.
    const cands = [
      'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac',
      'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg',
    ];
    for (const t of cands) {
      try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t; } catch (e) {}
    }
    return '';
  };

  const drawLoop = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / buf.length);
    const level = Math.max(4, Math.min(100, Math.round(rms * 180)));
    const now = performance.now();
    if (now - lastPushRef.current >= 90) {   // ~11 отсчётов/сек
      lastPushRef.current = now;
      peaksRef.current.push(level);
      setLiveBars((prev) => {
        const next = [...prev, level];
        return next.length > 40 ? next.slice(next.length - 40) : next;
      });
    }
    rafRef.current = requestAnimationFrame(drawLoop);
  };

  const startTick = () => {
    tickRef.current = setInterval(() => {
      const elapsed = (performance.now() - startedAtRef.current - pausedMsRef.current) / 1000;
      setSeconds(elapsed);
      if (elapsed >= MAX_SECONDS) stopAndSend();
    }, 200);
  };

  const cleanupAudio = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (e) {}
    try { audioCtxRef.current && audioCtxRef.current.close(); } catch (e) {}
    audioCtxRef.current = null; analyserRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const startRecording = async () => {
    if (disabled || recording || uploading) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setSnackbar({ open: true, message: 'Нет доступа к микрофону', severity: 'danger' });
      return;
    }
    streamRef.current = stream;
    chunksRef.current = []; peaksRef.current = []; setLiveBars([]);
    pausedMsRef.current = 0; setSeconds(0); setPaused(false);

    const mime = pickMime();
    let rec;
    try { rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); }
    catch (e) { rec = new MediaRecorder(stream); }
    mediaRecorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.start(200);

    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    audioCtxRef.current = ctx;
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    analyserRef.current = analyser;

    startedAtRef.current = performance.now();
    setRecording(true);
    startTick();
    rafRef.current = requestAnimationFrame(drawLoop);
  };

  const buildWaveform = (N = 48) => {
    const src = peaksRef.current;
    if (!src.length) return [];
    const out = [];
    const step = src.length / N;
    for (let i = 0; i < N; i++) {
      const a = Math.floor(i * step);
      const b = Math.max(Math.floor((i + 1) * step), a + 1);
      let m = 0, c = 0;
      for (let j = a; j < b && j < src.length; j++) { m += src[j]; c++; }
      out.push(c ? Math.round(m / c) : 0);
    }
    return out;
  };

  const finalizeBlob = () => new Promise((resolve) => {
    const rec = mediaRecorderRef.current;
    if (!rec) { resolve(null); return; }
    const type = rec.mimeType || 'audio/webm';
    if (rec.state === 'inactive') { resolve(new Blob(chunksRef.current, { type })); return; }
    rec.onstop = () => resolve(new Blob(chunksRef.current, { type }));
    try { rec.stop(); } catch (e) { resolve(new Blob(chunksRef.current, { type: 'audio/webm' })); }
  });

  const cancelRecording = () => {
    try { mediaRecorderRef.current && mediaRecorderRef.current.stop(); } catch (e) {}
    cleanupAudio();
    mediaRecorderRef.current = null;
    chunksRef.current = []; peaksRef.current = [];
    setRecording(false); setPaused(false); setSeconds(0); setLiveBars([]);
  };

  const togglePause = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (!paused && rec.state === 'recording') {
      try { rec.pause(); } catch (e) {}
      pauseStartRef.current = performance.now();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setPaused(true);
    } else if (paused && rec.state === 'paused') {
      try { rec.resume(); } catch (e) {}
      pausedMsRef.current += performance.now() - pauseStartRef.current;
      rafRef.current = requestAnimationFrame(drawLoop);
      setPaused(false);
    }
  };

  const stopAndSend = async () => {
    if (!recording) return;
    const dur = Math.max(1, Math.round((performance.now() - startedAtRef.current - pausedMsRef.current) / 1000));
    const waveform = buildWaveform(48);
    const blob = await finalizeBlob();
    cleanupAudio();
    mediaRecorderRef.current = null;
    setRecording(false); setPaused(false);
    if (!blob || blob.size === 0) { setSeconds(0); setLiveBars([]); return; }

    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true); setUploadPct(0);
    try {
      await (onSendVoice && onSendVoice(blob, { duration: dur, waveform }, {
        onProgress: (p) => setUploadPct(p),
        signal: controller.signal,
      }));
    } catch (e) {
      if (!controller.signal.aborted) {
        setSnackbar({ open: true, message: 'Не удалось отправить голосовое', severity: 'danger' });
      }
    } finally {
      setUploading(false); setUploadPct(0); abortRef.current = null;
      setSeconds(0); setLiveBars([]); peaksRef.current = [];
    }
  };

  const cancelUpload = () => {
    try { abortRef.current && abortRef.current.abort(); } catch (e) {}
    setUploading(false); setUploadPct(0);
  };

  // Освободить микрофон при размонтировании
  useEffect(() => () => { cleanupAudio(); }, []);

  // Миниатюры для картинок: вставил Ctrl+V — сразу видно, ЧТО именно вставилось.
  // Для обычных файлов превью не делаем: смысла нет, а память тратится.
  useEffect(() => {
    const next = {};
    attachments.slice(0, 40).forEach((file, i) => {
      if (file?.type?.startsWith('image/') && file.size <= CHAT_PREVIEW_SIZE) {
        try { next[i] = URL.createObjectURL(file); } catch (e) { /* не критично */ }
      }
    });
    setPreviews(next);
    return () => { Object.values(next).forEach((url) => URL.revokeObjectURL(url)); };
  }, [attachments]);

  const fmtTime = (s) => {
    const m = Math.floor(s / 60), ss = Math.floor(s % 60);
    return `${m}:${String(ss).padStart(2, '0')}`;
  };

  // Функция проверки файлов
  const validateFiles = (files) => {
    // Проверка размера каждого файла
    for (const file of files) {
      if (file.size > CHAT_FILE_SIZE) {
        setSnackbar({
          open: true,
          message: `Файл «${file.name}» превышает 2 ГиБ`,
          severity: 'danger'
        });
        return false;
      }
    }

    return true;
  };

  const handleClick = async () => {
    if (disabled || submitRef.current || (!attachments.length && !textAreaValue.trim())) return;
    submitRef.current = true;
    setMentionOpen(false);
    try {
      const done = await onSubmit(mentions, { originRect: captureRect(composerRef.current) });
      if (done !== false) setMentions([]);
    } finally { submitRef.current = false; }
  };

  const handleAttachClick = (type = 'any') => {
    if (disabled) return;
    if (type === 'image') {
      imageInputRef?.current?.click();
    } else {
      fileInputRef?.current?.click();
    }
  };

  const handleFileSelect = (event, type = 'any') => {
    if (disabled) { event.target.value = ''; return; }
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // Проверяем файлы перед добавлением
      if (!validateFiles(files)) {
        event.target.value = '';
        return;
      }
      
      if (type === 'image') {
        // Фильтруем только изображения
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        setAttachments(prev => [...prev, ...imageFiles]);
      } else {
        setAttachments(prev => [...prev, ...files]);
      }
    }
    
    // Сбрасываем значение input для возможности выбора тех же файлов снова
    event.target.value = '';
  };

  const handlePaste = (event) => {
    if (disabled) return;
    const items = event.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;
        // Скриншоты часто без имени — задаём читаемое, чтобы корректно отрисовать/отправить
        const named = file.name
          ? file
          : new File([file], `pasted-${Date.now()}.${(file.type.split('/')[1] || 'png')}`, { type: file.type });
        imageFiles.push(named);
      }
    }

    if (imageFiles.length === 0) return; // в буфере обычный текст — вставляем как есть
    event.preventDefault();             // не вставлять картинку как «мусорный» текст

    if (!validateFiles(imageFiles)) return;
    setAttachments(prev => [...prev, ...imageFiles]);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    // Esc выходит из режима правки и снимает ответ — привычное поведение
    if (event.key === 'Escape') {
      if (editing) { onCancelEdit?.(); event.preventDefault(); return; }
      if (replyTo) { onCancelReply?.(); event.preventDefault(); return; }
    }
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift + Enter - перенос строки
        event?.preventDefault();
        setTextAreaValue(prev => prev + '\n');
      } else {
        // Enter без Shift - отправка сообщения
        event?.preventDefault();
        handleClick();
      }
    }
  };

  const removeAttachment = (index) => {
    if (disabled) return;
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
    // Сообщаем родительскому компоненту об удалении файла
  };

  const handleTextareaChange = (event) => {
    const value = event.target.value;
    setTextAreaValue(value);

    // Запоминаем реальный textarea и позицию курсора (для вставки упоминания)
    taRef.current = event.target;
    const caret = event.target.selectionStart ?? value.length;
    caretRef.current = caret;

    // Активное @упоминание = "@" + слово без пробела перед курсором
    const upToCaret = value.slice(0, caret);
    const m = upToCaret.match(/@([^@\s]*)$/);
    if (m && participants.length > 0) {
      setMentionQuery(m[1]);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };


  const hasPayload = Boolean(textAreaValue.trim() || attachments.length > 0);

  return (
    <Box
      sx={(theme) => ({
        px: { xs: 1.5, md: 3 },
        pt: 1,
        pb: 1.5,
        // relative обязателен: подсказка @упоминаний теперь абсолютная и
        // раскрывается ВВЕРХ поверх ленты, а не раздвигает композер
        position: 'relative',
        zIndex: 4,
        borderTop: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(14px)',
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(20,24,35,0.8)'
          : 'rgba(255,255,255,0.86)',
      })}
    >
      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Typography level='title-lg' sx={{ textAlign: "center", width: "100%" }} color={snackbar.severity}>
          {snackbar.message}
        </Typography>
      </Snackbar>

      {/* Количество выбранных файлов; ограничения на число файлов нет */}
      {attachments.length > 0 && (
        <Typography
          level="body-xs"
          sx={{ mb: 0.5, px: 0.5, color: 'text.tertiary' }}
        >
          {attachments.length} файлов · отправим группами до {CHAT_BATCH_SIZE}
        </Typography>
      )}

      {/* Превью вложений — плитки вместо badge-строк: видно тип файла,
          размер и понятно, куда нажать, чтобы убрать */}
      {attachments.length > 0 && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
        >
          {attachments.map((file, index) => {
            const isImage = file.type?.startsWith('image/');
            const kb = file.size / 1024;
            const size = kb >= 1024 ? `${(kb / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(kb))} КБ`;
            return (
              <Box
                key={`${file.name}-${index}`}
                component={motion.div}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={MOTION.spring}
                sx={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 0.875,
                  pl: 0.875, pr: 3, py: 0.625,
                  maxWidth: 210, minWidth: 0,
                  borderRadius: '12px',
                  backgroundColor: 'background.level1',
                  border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', lineHeight: 0,
                    backgroundColor: isImage ? 'rgba(16,185,129,0.14)' : 'rgba(99,102,241,0.14)',
                    fontSize: 14,
                  }}
                >
                  {isImage && previews[index] ? (
                    <img
                      src={previews[index]}
                      alt={file.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (isImage ? '🖼' : '📄')}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body-xs" sx={{ ...ellipsis, fontWeight: 600, fontSize: 12 }}>
                    {file.name}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: 10.5 }}>
                    {size}
                  </Typography>
                </Box>
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  disabled={disabled}
                  onClick={() => removeAttachment(index)}
                  sx={{
                    position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                    '--IconButton-size': '22px', borderRadius: '50%',
                    color: 'text.tertiary',
                    '&:hover': { backgroundColor: 'danger.softBg', color: 'danger.500' },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Выпадашка упоминаний. Лежит АБСОЛЮТНО поверх ленты: раньше она была
          в общем потоке и при вводе «@» раздвигала композер, а вместе с ним
          подпрыгивал весь чат. */}
      {mentionOpen && mentionOptions.length > 0 && (
        <Sheet
          component={motion.div}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: MOTION.fast, ease: MOTION.ease }}
          variant="outlined"
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: { xs: 12, md: 24 },
            right: { xs: 12, md: 24 },
            mb: 1,
            zIndex: 20,
            borderRadius: '16px',
            maxHeight: 240,
            overflowY: 'auto',
            p: 0.5,
            boxShadow: 'lg',
            backdropFilter: 'blur(12px)',
          }}
        >
          {mentionOptions.slice(0, 8).map((p) => (
            <Box key={`${p.kind}-${p.id}`} onClick={() => pickMention(p)}
              sx={{ px: 1.25, py: 0.75, borderRadius: '12px', cursor: 'pointer',
                    transition: 'background-color .12s',
                    '&:hover': { backgroundColor: 'background.level1' } }}>
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>{p.name}</Typography>
              {p.position ? (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {Array.isArray(p.position) ? p.position.join(', ') : p.position}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Sheet>
      )}

      {/* Режим правки сообщения */}
      {editing && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          sx={{
            mb: 1, px: 1.25, py: 0.875, borderRadius: '14px',
            display: 'flex', alignItems: 'center', gap: 1.25,
            backgroundColor: 'rgba(245,158,11,0.12)',
            borderLeft: '3px solid', borderColor: 'warning.400',
          }}
        >
          <EditRoundedIcon sx={{ fontSize: 17, color: 'warning.500', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'warning.600' }}>
              Изменение сообщения
            </Typography>
            <Typography level="body-xs" sx={{ ...ellipsis, color: 'text.secondary' }}>
              {editing?.content || ''}
            </Typography>
          </Box>
          <IconButton
            size="sm" variant="plain" color="neutral"
            onClick={onCancelEdit}
            sx={{ borderRadius: '50%' }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Превью ответа */}
      {replyTo && !editing && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          sx={{
            mb: 1, px: 1.25, py: 0.875, borderRadius: '14px',
            display: 'flex', alignItems: 'center', gap: 1.25,
            backgroundColor: 'rgba(99,102,241,0.09)',
            borderLeft: '3px solid', borderColor: 'primary.400',
          }}
        >
          <ReplyRoundedIcon sx={{ fontSize: 17, color: 'primary.500', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-xs" sx={{ ...ellipsis, fontWeight: 700, color: 'primary.600' }}>
              {`${replyTo?.sender?.last_name || ''} ${replyTo?.sender?.name || ''}`.trim() || 'Сообщение'}
            </Typography>
            <Typography level="body-xs" sx={{ ...ellipsis, color: 'text.secondary' }}>
              {replyTo?.content || 'вложение'}
            </Typography>
          </Box>
          <IconButton size="sm" variant="plain" color="neutral" onClick={onCancelReply} sx={{ borderRadius: '50%' }}>
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      <Sheet
        variant="plain"
        sx={(theme) => ({
          py: 0.5,
          px: 0.75,
          gap: 1,
          borderRadius: `${CHAT.composerRadius}px`,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,34,71,0.07)',
          backgroundColor: theme.palette.mode === 'dark' ? '#20263a' : '#f2f4fa',
          transition: 'border-color .18s, box-shadow .18s, background-color .18s',
          '&:focus-within': {
            borderColor: 'rgba(99,102,241,0.45)',
            backgroundColor: theme.palette.mode === 'dark' ? '#242b42' : '#fff',
            boxShadow: '0 0 0 3px rgba(99,102,241,0.12)',
          },
        })}
      >
        {(recording || uploading) ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', minHeight: 40 }}>
            {uploading ? (
              <>
                <Tooltip title="Отменить отправку">
                  <IconButton size="sm" variant="soft" color="danger" onClick={cancelUpload}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress determinate value={uploadPct} sx={{ '--LinearProgress-radius': '8px' }} />
                </Box>
                <Typography level="body-sm" sx={{ minWidth: 42, textAlign: 'right' }}>{uploadPct}%</Typography>
              </>
            ) : (
              <>
                <Tooltip title="Отменить">
                  <IconButton size="sm" variant="soft" color="danger" onClick={cancelRecording}>
                    <DeleteRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '2px', height: 32, overflow: 'hidden' }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', bgcolor: 'danger.500', flexShrink: 0, mr: 0.5,
                    animation: paused ? 'none' : 'vpulse 1s infinite',
                    '@keyframes vpulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                  }} />
                  {liveBars.map((v, i) => (
                    <Box key={i} sx={{
                      width: 3, borderRadius: 2, bgcolor: 'primary.solidBg',
                      height: `${Math.max(10, v)}%`, alignSelf: 'center', opacity: 0.85,
                    }} />
                  ))}
                </Box>
                <Typography level="body-sm" sx={{ minWidth: 46, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtTime(seconds)}
                </Typography>
                <Tooltip title={paused ? 'Продолжить' : 'Пауза'}>
                  <IconButton size="sm" variant="soft" color="neutral" onClick={togglePause}>
                    {paused ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Отправить голосовое">
                  <IconButton size="sm" variant="solid" color="primary" onClick={stopAndSend}>
                    <SendRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        ) : (
        <Stack spacing={0.5} direction={'row'} sx={{ alignItems: 'center' }}>
          {/* Скрытые input'ы для файлов */}
          <input
            type="file"
            disabled={disabled}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => handleFileSelect(e, 'any')}
            multiple
          />
          <input
            type="file"
            disabled={disabled}
            ref={imageInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={(e) => handleFileSelect(e, 'image')}
            multiple
          />

          {/* Кнопки действий */}
          <Stack direction="row" spacing={0.5}>
            <Tooltip
              title="Прикрепить файлы — до 2 ГиБ каждый"
              placement="top"
            >
              <IconButton
                component={motion.button}
                whileHover={{ scale: 1.08, rotate: -12 }}
                whileTap={{ scale: 0.88 }}
                transition={MOTION.spring}
                variant="plain"
                color="neutral"
                disabled={disabled}
                onClick={() => handleAttachClick('any')}
                sx={(theme) => ({
                  '--IconButton-size': '38px',
                  borderRadius: '50%',
                  // Иконки в композере красим через --Icon-color: у Joy обычный
                  // color до самой иконки не доходит, и они оставались чёрными.
                  ...iconColor(theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.55)' : 'rgba(71,84,103,0.62)'),
                  '&:hover': {
                    backgroundColor: 'background.level2',
                    ...iconColor('var(--joy-palette-primary-500)'),
                  },
                  '&.Mui-disabled': { ...iconColor('rgba(148,163,184,0.5)') },
                })}
              >
                <AttachFileRoundedIcon sx={{ fontSize: 20, transform: 'rotate(-45deg)' }} />
              </IconButton>
            </Tooltip>

            {/* <Tooltip title="Прикрепить фото" placement="top">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => handleAttachClick('image')}
              >
                <InsertPhotoRoundedIcon />
              </IconButton>
            </Tooltip> */}

            {/* <Tooltip title="Смайлики" placement="top">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
              >
                <InsertEmoticonRoundedIcon />
              </IconButton>
            </Tooltip> */}
          </Stack>

          {/* Поле ввода. ref нужен анимации отправки: из этой рамки
              «вылетает» текст, сжимаясь в комочек. */}
          <FormControl disabled={disabled} ref={composerRef} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Textarea
              disabled={disabled}
              placeholder="Введите сообщение..."
              aria-label="Сообщение"
              ref={textAreaRef}
              color='neutral'
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              value={textAreaValue}
              minRows={1}
              maxRows={6}
              variant="plain"
              sx={{
                borderRadius: '16px',
                p: 0,
                backgroundColor: 'transparent',
                resize: 'none',
                '--Textarea-focusedThickness': '0px',
                '&::before': { display: 'none' },
                '&:focus-within': { boxShadow: 'none' },
                '& textarea': {
                  px: 0.5,
                  py: 0.875,
                  fontSize: 14.5,
                  lineHeight: 1.45,
                  maxHeight: '132px',
                },
                '& textarea::placeholder': { opacity: 0.55, fontSize: 14.5 },
              }}
            />
          </FormControl>

          {/* Кнопка: отправка (если есть текст/вложения) или запись голоса */}
          <Stack sx={{ alignSelf: 'flex-end' }}>
            {hasPayload ? (
              <Tooltip title="Отправить (Enter)" placement="top">
                <IconButton
                  component={motion.button}
                  key="send"
                  initial={{ scale: 0.6, rotate: -25, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={MOTION.spring}
                  whileHover={{ scale: 1.07 }}
                  whileTap={{ scale: 0.86 }}
                  disabled={disabled}
                  onClick={handleClick}
                  sx={{
                    '--IconButton-size': '38px',
                    borderRadius: '50%',
                    // Белая стрелка: без --Icon-color Joy рисовала её чёрной
                    // поверх фиолетового градиента.
                    ...iconColor('#fff'),
                    background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
                    boxShadow: '0 3px 10px rgba(99,102,241,0.38)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5b5ef0 0%, #7331e6 100%)',
                      ...iconColor('#fff'),
                    },
                  }}
                >
                  <SendRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Записать голосовое" placement="top">
                <IconButton
                  component={motion.button}
                  key="mic"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={MOTION.spring}
                  whileHover={{ scale: 1.07 }}
                  whileTap={{ scale: 0.86 }}
                  disabled={disabled}
                  onClick={startRecording}
                  sx={(theme) => ({
                    '--IconButton-size': '38px',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    ...iconColor(theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.55)' : 'rgba(71,84,103,0.62)'),
                    '&:hover': {
                      backgroundColor: 'background.level2',
                      ...iconColor('var(--joy-palette-primary-500)'),
                    },
                  })}
                >
                  <MicRoundedIcon sx={{ fontSize: 21 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        )}
      </Sheet>

      {/* Подсказка появляется, только когда есть что отправлять —
          раньше она висела постоянно и забирала внимание */}
      {hasPayload && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          sx={{ textAlign: 'center', mt: 0.5 }}
        >
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: 11 }}>
            {editing
              ? 'Enter — сохранить · Esc — отменить правку'
              : 'Enter — отправить · Shift+Enter — перенос строки'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
